/**
 * Pure cost-forecast math — no I/O, no framework. Shared by the bmai-web
 * ViewModel now and ported to the bmai-insights compute service later so both
 * produce identical numbers. Projects month-end spend from a metered daily
 * run-rate averaged over ACTIVE days only (days with real usage — weekends at a
 * working company often have none), plus the flat monthly seat/subscription cost.
 */

/** One day of metered (allocated) spend in the current month. */
export interface DailyCostPoint {
    /** ISO day bucket, e.g. `2026-07-14`. */
    date: string;
    cost: number;
}

/** A completed prior month's total spend (metered + flat), for the comparison + chart. */
export interface MonthlyCostPoint {
    label: string;
    total: number;
}

export interface ForecastInput {
    now: Date;
    /** Metered allocated spend per day for the current month, day 1 → today. */
    currentMonthDaily: DailyCostPoint[];
    /** Flat seat/subscription $ committed for the current month. */
    flatMonthly: number;
    /** Completed prior months (oldest → newest), each total = metered + flat. */
    trailingMonths: MonthlyCostPoint[];
}

export interface ForecastSummary {
    meteredMtd: number;
    flatMonthly: number;
    mtdActual: number;
    avgActiveDay: number;
    activeDays: number;
    remainingActiveDays: number;
    projectedMetered: number;
    projectedMonth: number;
    lastMonthTotal: number | null;
    deltaVsLastMonth: number | null;
    deltaPctVsLastMonth: number | null;
}

export interface CumulativePoint {
    date: string;
    /** Cumulative actual spend through this day; null on projected-only days. */
    actual: number | null;
    /** Cumulative projected spend; null before today (the two series meet at today). */
    projected: number | null;
}

export interface MonthBar {
    label: string;
    total: number;
    projected: boolean;
}

export interface ForecastResult {
    summary: ForecastSummary;
    cumulative: CumulativePoint[];
    months: MonthBar[];
}

function isWeekday(date: Date): boolean {
    const day = date.getUTCDay();
    return day >= 1 && day <= 5;
}

function endOfMonthDay(now: Date): number {
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).getUTCDate();
}

/** Weekdays strictly after today through the end of the current month — the active-day estimate for the remainder. */
function remainingWeekdays(now: Date): number {
    const year = now.getUTCFullYear();
    const month = now.getUTCMonth();
    const lastDay = endOfMonthDay(now);
    let count = 0;
    for (let day = now.getUTCDate() + 1; day <= lastDay; day += 1) {
        if (isWeekday(new Date(Date.UTC(year, month, day)))) count += 1;
    }
    return count;
}

function monthLabel(now: Date): string {
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
}

export function computeForecast(input: ForecastInput): ForecastResult {
    const { now, currentMonthDaily, flatMonthly, trailingMonths } = input;
    const sorted = [...currentMonthDaily].sort((a, b) => a.date.localeCompare(b.date));

    const meteredMtd = sorted.reduce((sum, point) => sum + point.cost, 0);
    const activeDays = sorted.filter((point) => point.cost > 0).length;
    const avgActiveDay = activeDays > 0 ? meteredMtd / activeDays : 0;
    const remainingActiveDays = remainingWeekdays(now);
    const projectedMetered = meteredMtd + avgActiveDay * remainingActiveDays;
    const projectedMonth = projectedMetered + flatMonthly;
    const mtdActual = meteredMtd + flatMonthly;

    const lastMonthTotal = trailingMonths.length > 0 ? trailingMonths[trailingMonths.length - 1]!.total : null;
    const deltaVsLastMonth = lastMonthTotal === null ? null : projectedMonth - lastMonthTotal;
    const deltaPctVsLastMonth = lastMonthTotal === null || lastMonthTotal === 0 ? null : (projectedMonth - lastMonthTotal) / lastMonthTotal;

    const cumulative: CumulativePoint[] = [];
    let runningActual = 0;
    for (const point of sorted) {
        runningActual += point.cost;
        cumulative.push({ date: point.date, actual: runningActual, projected: null });
    }
    if (cumulative.length > 0) {
        cumulative[cumulative.length - 1] = { ...cumulative[cumulative.length - 1]!, projected: runningActual };
    }
    let runningProjected = runningActual;
    const year = now.getUTCFullYear();
    const month = now.getUTCMonth();
    const lastDay = endOfMonthDay(now);
    for (let day = now.getUTCDate() + 1; day <= lastDay; day += 1) {
        const date = new Date(Date.UTC(year, month, day));
        if (isWeekday(date)) runningProjected += avgActiveDay;
        cumulative.push({ date: date.toISOString().slice(0, 10), actual: null, projected: runningProjected });
    }

    const months: MonthBar[] = [
        ...trailingMonths.map((entry) => ({ label: entry.label, total: entry.total, projected: false })),
        { label: monthLabel(now), total: projectedMonth, projected: true },
    ];

    return {
        summary: {
            meteredMtd,
            flatMonthly,
            mtdActual,
            avgActiveDay,
            activeDays,
            remainingActiveDays,
            projectedMetered,
            projectedMonth,
            lastMonthTotal,
            deltaVsLastMonth,
            deltaPctVsLastMonth,
        },
        cumulative,
        months,
    };
}

/** One prior month's cumulative spend indexed by day-of-month — a faint comparison line behind the current month's trajectory. */
export interface MonthComparison {
    label: string;
    points: { day: number; value: number }[];
}

/**
 * Builds cumulative-within-month spend curves for the months preceding the current one, on a shared
 * day-of-month axis, so the current month's trajectory can be read against how recent months ran.
 * `lookbackMonths` counts the current month, so `1` yields no comparison lines.
 */
export function buildMonthlyComparison(daily: { date: string; cost: number }[], now: Date, lookbackMonths: number): MonthComparison[] {
    if (lookbackMonths <= 1) return [];
    const currentYear = now.getUTCFullYear();
    const currentMonth = now.getUTCMonth();
    const byMonth = new Map<string, { year: number; month: number; days: Map<number, number> }>();
    for (const point of daily) {
        const date = new Date(`${point.date}T00:00:00.000Z`);
        if (Number.isNaN(date.getTime())) continue;
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth();
        if (year === currentYear && month === currentMonth) continue;
        const key = `${year}-${month}`;
        let entry = byMonth.get(key);
        if (entry === undefined) {
            entry = { year, month, days: new Map() };
            byMonth.set(key, entry);
        }
        entry.days.set(date.getUTCDate(), (entry.days.get(date.getUTCDate()) ?? 0) + point.cost);
    }
    const months = [...byMonth.values()].sort((a, b) => a.year - b.year || a.month - b.month);
    return months.map((entry) => {
        const label = new Date(Date.UTC(entry.year, entry.month, 1)).toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
        const lastDay = entry.days.size > 0 ? Math.max(...entry.days.keys()) : 0;
        const points: { day: number; value: number }[] = [];
        let cumulative = 0;
        for (let day = 1; day <= lastDay; day += 1) {
            cumulative += entry.days.get(day) ?? 0;
            points.push({ day, value: cumulative });
        }
        return { label, points };
    });
}
